import React, { useState, useCallback, useMemo, useRef } from "react";
import { Alert } from "react-bootstrap";
import ConfigurablePdfViewer from "components/PdfViewer/ConfigurablePdfViewer";
import { Card } from "react-bootstrap";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
import MemberSwitchList from "components/UnprocessedFaxList/MemberSwitchList";
import DiscardFax from "components/UnprocessedFaxDetails/DiscardFax";
import CompleteLinkingButton from "components/UnprocessedFaxDetails/CompleteLinkingButton";
import { UnprocessedFaxConstants } from "lookup/UnprocessedFaxConstants";
import { isStringEmptyOrNull } from "services/UtilityService";
import Select from "react-select";
import { useUnprocessedFaxDetails } from "./useUnprocessedFaxDetails";
import PrescriberListV2 from "components/UnprocessedFaxList/PrescriberListV2";
import ConfirmationModal from "components/ConfirmationModal";
import { UNPROCESSED_FAX_PROGRAM_TYPE, FAX_DETAILS_UI_RULES } from "lookup/UnprocessedFaxListConstantsV2";
import withRouter from "utils/withRouter";

const getFaxTypeKey = (value = "") => {
  const normalized = value.toLowerCase();
  if (normalized === "unknown") return UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN;
  if (normalized === "nonswitch" || normalized === "non-switch") return UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH;
  return UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH;
};

const UnprocessedFaxDetails = (props) => {
  const { faxInfoId, faxType } = props.match.params;
  const history = props.history;
  const [disableLinking, setDisableLinking] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [hiddenPages, setHiddenPages] = useState([]);
  const [resetPrescriberSelection, setResetPrescriberSelection] = useState(0);
  const [resetSwitchSelection, setResetSwitchSelection] = useState(0);
  const formikRef = useRef(null);
  const normalizedFaxType = useMemo(() => getFaxTypeKey(faxType), [faxType]);
  const faxTypeConfig = useMemo(
    () => FAX_DETAILS_UI_RULES[normalizedFaxType] || FAX_DETAILS_UI_RULES[UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH],
    [normalizedFaxType]
  );
  const linkFieldLabels = faxTypeConfig.linkFieldLabels;

  // Use custom hook for all state and operations
  const {
    loading,
    error,
    unprocessedFaxDetails,
    faxPriority,
    note,
    noteObject,
    allAvailableTags,
    allSelectedTags,
    selectedTags,
    tagsChanged,
    saveTagsLoading,
    saveNoteLoading,
    updateNoteLoading,
    deleteNoteLoading,
    linkUnprocessedFaxToSwitch: linkFaxToSwitch,
    linkAndArchiveFax,
    linkAndArchiveLoading,
    discardFax,
    completePageLinking,
    linkedPages: hookLinkedPages,
    setPriorityOfUnprocessedFax,
    saveNote,
    updateNote,
    deleteNote,
    saveTags,
    handleTagsChange,
    setNote,
  } = useUnprocessedFaxDetails(faxInfoId, faxType);

  // Merge hookLinkedPages (fax-archive backed) with locally hidden pages for NON_SWITCH + SWITCH
  const allHiddenPages = useMemo(() => {
    if (
      (faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH ||
        faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH) &&
      hookLinkedPages.length > 0
    ) {
      const combined = new Set([...hiddenPages, ...hookLinkedPages]);
      return Array.from(combined).sort((a, b) => a - b);
    }
    return hiddenPages;
  }, [hiddenPages, hookLinkedPages, faxTypeConfig.key]);

  // Check if any pages are linked for NON_SWITCH
  const hasLinkedPages = useMemo(() => {
    // NON_SWITCH uses hookLinkedPages; SWITCH uses local hiddenPages populated after linking requests.
    return hookLinkedPages.length > 0 || hiddenPages.length > 0;
  }, [hookLinkedPages, hiddenPages]);

  // Handle linking fax to switch
  const handleLinkUnprocessedFaxToSwitch = useCallback(
    async (values, setSubmitting) => {
      // For NON_SWITCH and SWITCH, show confirmation modal first (match NON_SWITCH UX)
      if (
        faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH ||
        faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH
      ) {
        setPendingSubmit({ values, setSubmitting });
        setShowConfirmModal(true);
        return;
      }
      
      // For other types, proceed normally
      setSubmitting(true);
      try {
        const { faxId, linkPages } = values;
        await linkFaxToSwitch(faxId, linkPages);
      } finally {
        setSubmitting(false);
      }
    },
    [linkFaxToSwitch, faxTypeConfig.key]
  );

  // Helper function to parse page numbers from comma-separated string
  const parsePageNumbers = useCallback((pageString) => {
    if (!pageString || typeof pageString !== 'string') return [];
    return pageString
      .split(',')
      .map(page => parseInt(page.trim(), 10))
      .filter(page => !isNaN(page) && page > 0);
  }, []);

  // Helper function to check if all fax pages have been linked
  const checkIfAllPagesLinked = useCallback(
    (linkPages = [], hiddenPages = [], totalPages) => {
      if (!Number.isInteger(totalPages) || totalPages <= 0) return false;
  
      // Collect already linked pages
      const linkedPages = new Set(hiddenPages);
  
      // Collect pages from the current linking request
      linkPages.forEach(({ referenceField, additionalField }) => {
        // For SWITCH: referenceField is the primary page index; treat it as linked too.
        const refPage = parseInt(String(referenceField), 10);
        if (!isNaN(refPage) && refPage > 0) linkedPages.add(refPage);
        if (!additionalField) return;
  
        parsePageNumbers(String(additionalField))
          .forEach(page => linkedPages.add(page));
      });
  
      // Verify all pages are linked
      return Array.from({ length: totalPages }, (_, i) =>
        linkedPages.has(i + 1)
      ).every(Boolean);
    },
    [parsePageNumbers]
  );

  const onChangeLinkPrescribers = useCallback((prescriberNpis, values, setValues) => {
    // update dynamic form
    const linkPages = [];
    prescriberNpis.forEach((prescriberNpi) => {
      const prescriberInfo = values.linkPages.find(
        (x) => x.identifier === prescriberNpi
      );
      if (prescriberInfo) {
        linkPages.push(prescriberInfo);
      } else {
        linkPages.push({
          isUpsideDown: false,
          overrideAssociatedSwitch: false,
          identifier: prescriberNpi,
          referenceField: null,
          additionalField: null,
        });
      }
    });
    setValues({ ...values, linkPages });
    if (prescriberNpis.length > 0) {
      setDisableLinking(false);
    } else {
      setDisableLinking(true);
    }
  }, []);

  const onChangeLinkSwitches = useCallback((globalSwitchIds, values, setValues) => {
    const linkPages = [];
    globalSwitchIds.forEach((globalSwitchId) => {
      const switchInfo = values.linkPages.find(
        (x) => x.identifier === globalSwitchId
      );
      if (switchInfo) {
        linkPages.push(switchInfo);
      } else {
        linkPages.push({
          isUpsideDown: false,
          overrideAssociatedSwitch: false,
          identifier: globalSwitchId,
          referenceField: null,
          additionalField: null,
        });
      }
    });
    setValues({ ...values, linkPages });
    if (globalSwitchIds.length > 0) {
      setDisableLinking(false);
    } else {
      setDisableLinking(true);
    }
  }, []);

  // Handle confirmation modal confirm
  const handleConfirmLinkAndArchive = useCallback(async () => {
    if (!pendingSubmit) return;
    
    setShowConfirmModal(false);
    const { values, setSubmitting } = pendingSubmit;
    setPendingSubmit(false);
    
    setSubmitting(true);
    const { faxId, linkPages } = values;
    
    // Check if all pages are linked
    const isFaxCompletelyLinked = checkIfAllPagesLinked(
      linkPages,
      allHiddenPages,
      unprocessedFaxDetails?.noOfPages
    );
    
    try {
      if (faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH) {
        await linkAndArchiveFax(faxId, linkPages, isFaxCompletelyLinked);
      } else if (faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH) {
        await linkFaxToSwitch(faxId, linkPages, isFaxCompletelyLinked);
      } else {
        await linkFaxToSwitch(faxId, linkPages);
      }
      
      // Extract and add linked pages to hidden pages (NON_SWITCH + SWITCH)
      if (
        faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH ||
        faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH
      ) {
        setHiddenPages((prevHiddenPages) => {
          const pagesToHide = new Set(prevHiddenPages);
          linkPages.forEach((linkPage) => {
            const refPage = parseInt(String(linkPage.referenceField), 10);
            if (!isNaN(refPage) && refPage > 0) pagesToHide.add(refPage);
            if (linkPage.additionalField) {
              const parsedPages = parsePageNumbers(String(linkPage.additionalField));
              parsedPages.forEach((page) => pagesToHide.add(page));
            }
          });
          return Array.from(pagesToHide).sort((a, b) => a - b);
        });
      }
      
      if (formikRef.current) {
        const resetValues = {
          faxId: faxInfoId,
          linkPages: [],
        };
        formikRef.current.resetForm({
          values: resetValues
        });
        // Clear prescriber selection in the form
        onChangeLinkPrescribers([], resetValues, formikRef.current.setValues);
        // Clear switch table checkboxes and sync link rows (SWITCH fax type)
        onChangeLinkSwitches([], resetValues, formikRef.current.setValues);
      }
      
      setResetPrescriberSelection(prev => prev + 1);
      setResetSwitchSelection((prev) => prev + 1);
      setDisableLinking(true);
    } catch (error) {} finally {
      setSubmitting(false);
    }
  }, [pendingSubmit, linkAndArchiveFax, linkFaxToSwitch, faxTypeConfig.key, parsePageNumbers, faxInfoId, onChangeLinkPrescribers, onChangeLinkSwitches, checkIfAllPagesLinked, allHiddenPages, unprocessedFaxDetails?.noOfPages]);

  // Handle confirmation modal cancel
  const handleCancelLinkAndArchive = useCallback(() => {
    setShowConfirmModal(false);
    setPendingSubmit(false);
  }, []);

  // Handle save or update note
  const handleSaveOrUpdateNote = useCallback(() => {
    if (noteObject?.uflNoteUuid?.length > 0) {
      updateNote();
    } else {
      saveNote();
    }
  }, [noteObject, updateNote, saveNote]);

  // Handle priority change
  const handlePriorityChange = useCallback(
    (e) => {
      const priority = e.target.value;
      setPriorityOfUnprocessedFax(priority);
    },
    [setPriorityOfUnprocessedFax]
  );

  const handleSaveTagsAction = useCallback(async () => {
    await saveTags();
    if (faxTypeConfig.navigateBackAfterTagSave) {
      history.goBack();
    }
  }, [saveTags, faxTypeConfig.navigateBackAfterTagSave, history]);

  // Validation schema - must be defined before early returns to follow Rules of Hooks
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        linkPages: Yup.array().of(
          Yup.object().shape({
            isUpsideDown: Yup.boolean(),
            overrideAssociatedSwitch: Yup.boolean(),
            identifier: Yup.string()
              .typeError(`Enter a valid ${linkFieldLabels.identifier}`)
              .required(`${linkFieldLabels.identifier} is required`),
            // For NON_SWITCH, allow a long numeric Fax Reference ID (like Prescriber NPI);
            // for other fax types, keep the existing page-based numeric validation.
            referenceField:
              faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH
                ? Yup.string()
                    .matches(/^\d+$/, `${linkFieldLabels.referenceField} must be a number`)
                    .required(`${linkFieldLabels.referenceField} is required`)
                : Yup.number()
                    .required(`${linkFieldLabels.referenceField} is required`)
                    .min(
                      1,
                      `${linkFieldLabels.referenceField} must be greater than or equal to 1`
                    )
                    .max(
                      unprocessedFaxDetails?.noOfPages || 999,
                      `${linkFieldLabels.referenceField} must be less than or equal to ${
                        unprocessedFaxDetails?.noOfPages || 999
                      }`
                    ),
            additionalField:
              faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH
                ? Yup.string()
                    .required(`${linkFieldLabels.additionalField} is required`)
                : Yup.string()
                    .typeError(`Enter valid ${linkFieldLabels.additionalField}`)
                    .nullable()
                    .notRequired(),
          })
        ),
      }),
    [linkFieldLabels, unprocessedFaxDetails?.noOfPages, faxTypeConfig.key]
  );

  // Loading and error states
  if (loading) {
    return "Fetching Fax Details ... ";
  }

  if (error || !unprocessedFaxDetails) {
    return error || "Failed to fetch Fax details";
  }

    return (
      <Container fluid>
        <Row>
          <Col md={5}>
            <Breadcrumb>
              <Breadcrumb.Item href={`/unprocessed-fax/program-type-list`}>
                Unprocessed Fax List
              </Breadcrumb.Item>
              <Breadcrumb.Item active>Fax {faxInfoId}</Breadcrumb.Item>
            </Breadcrumb>
            {faxTypeConfig.showPriority && (
              <Row className="tiny-top-spacer">
                <Col
                  id="dispositionForContainer"
                  md={12}
                  style={{ fontSize: "1.5em" }}
                >
                  <span className="text-muted">{faxTypeConfig.headingText}</span>
                </Col>
                <Col md={12} className="tiny-top-spacer">
                  <Form.Group>
                    <Form.Label>Fax Priority</Form.Label>
                    <Form.Select
                      value={faxPriority}
                      onChange={handlePriorityChange}
                    >
                      <option value="">None</option>
                      {Object.entries(
                        UnprocessedFaxConstants.UNPROCESSED_FAX_PRIORITY
                      ).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}
            {faxTypeConfig.showAssociatedSwitch &&
            !isStringEmptyOrNull(unprocessedFaxDetails?.associatedSwitch) ? (
              <Row className="tiny-top-spacer">
                <Col md={12}>
                  <span className="text-muted">
                    Currently Associated With:{" "}
                    {unprocessedFaxDetails?.associatedSwitch}
                  </span>
                </Col>
              </Row>
            ) : null}
            {faxTypeConfig.showTags && (
              <>
                <hr />
                <Row className="tiny-top-spacer align-items-end">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Tags</Form.Label>
                      <Select
                        isMulti
                        options={allAvailableTags.map(tag => ({ value: tag, label: tag }))}
                        value={selectedTags && selectedTags.length > 0 
                          ? selectedTags.map(tag => ({ value: tag, label: tag }))
                          : []}
                        onChange={(selectedOptions) => {
                          const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                          handleTagsChange(selectedValues);
                        }}
                        placeholder="Select or remove tags"
                        isClearable
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12} className="d-flex justify-content-end">
                    <Button
                      variant="primary"
                      onClick={handleSaveTagsAction}
                      disabled={!tagsChanged || saveTagsLoading}
                    >
                      {saveTagsLoading
                        ? "Saving..."
                        : faxTypeConfig.tagsButtonLabel}
                    </Button>
                  </Col>
                </Row>
              </>
            )}
            {faxTypeConfig.showDiscard && !faxTypeConfig.showLinkSection && (
              <>
                <hr />
                <Row>
                  <Col className="d-grid gap-2">
                    <DiscardFax
                      disallowEditing={false}
                      discardFax={discardFax}
                      faxId={faxInfoId}
                    />
                  </Col>
                </Row>
              </>
            )}
            {faxTypeConfig.showLinkSection && (
              <>
                <hr />
                <Formik
              innerRef={formikRef}
              enableReinitialize
              initialValues={{
                faxId: faxInfoId,
                linkPages: [],
              }}
              onSubmit={(values, { setSubmitting }) => {
                handleLinkUnprocessedFaxToSwitch(values, setSubmitting);
              }}
              validationSchema={validationSchema}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setValues,
                setFieldValue,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <FieldArray
                    name="linkPages"
                    render={(arrayHelpers) => (
                      <>
                        {values.linkPages.map((linkPage, i) => (
                          <Row key={i}>
                            <Col md={5}>
                              <Form.Group>
                                <Form.Label>
                                  {linkFieldLabels.identifier}
                                </Form.Label>
                                <Form.Control
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={linkPage.identifier}
                                  name={`linkPages.${i}.identifier`}
                                  disabled={true}
                                />
                                <Form.Text className="text-small text-danger">
                                  {errors.linkPages &&
                                    touched.linkPages &&
                                    errors.linkPages[i] &&
                                    touched.linkPages[i] &&
                                    errors.linkPages[i]
                                      .identifier &&
                                    touched.linkPages[i]
                                      .identifier &&
                                    errors.linkPages[i].identifier}
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label>
                                  {linkFieldLabels.referenceField}
                                </Form.Label>
                                <Form.Control
                                  type={faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH ? "text" : "number"}
                                  {...(faxTypeConfig.key !== UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH && {
                                    step: 1,
                                    min: 1,
                                    max: unprocessedFaxDetails.noOfPages,
                                  })}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={linkPage.referenceField || ""}
                                  name={`linkPages.${i}.referenceField`}
                                ></Form.Control>
                                <Form.Text className="text-small text-danger">
                                  {errors.linkPages &&
                                    touched.linkPages &&
                                    errors.linkPages[i] &&
                                    touched.linkPages[i] &&
                                    errors.linkPages[i].referenceField &&
                                    touched.linkPages[i].referenceField &&
                                    errors.linkPages[i].referenceField}
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label>
                                  {linkFieldLabels.additionalField}
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={linkPage.additionalField || ""}
                                  name={`linkPages.${i}.additionalField`}
                                  placeholder="e.g., 1,2,3"
                                ></Form.Control>
                                <Form.Text className="text-muted" style={{ fontSize: "0.875rem" }}>
                                  Enter page numbers as shown on the PDF (comma-separated)
                                </Form.Text>
                                <Form.Text className="text-small text-danger">
                                  {errors.linkPages &&
                                    touched.linkPages &&
                                    errors.linkPages[i] &&
                                    touched.linkPages[i] &&
                                    errors.linkPages[i].additionalField &&
                                    touched.linkPages[i].additionalField &&
                                    errors.linkPages[i].additionalField}
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={6} className="mt-2">
                              <Form.Group style={{ marginBottom: 0 }}>
                                <Form.Label>Orientation</Form.Label>
                                <Form.Check
                                  type="checkbox"
                                  value={linkPage.isUpsideDown}
                                  name={`linkPages.${i}.isUpsideDown`}
                                  label="Is Upside Down"
                                  onChange={handleChange}
                                  checked={linkPage.isUpsideDown}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        ))}
                        <hr />
                      </>
                    )}
                  />
                  <Row>
                    <Col md={6} className="d-grid gap-2">
                      <Button
                        id="unprocessed-fax-btn-link"
                        block
                        variant="success"
                        type="submit"
                        disabled={
                          isSubmitting ||
                          disableLinking ||
                          (faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH &&
                            (linkAndArchiveLoading ||
                              values.linkPages.some(
                                (page) =>
                                  !page.referenceField || !page.additionalField
                              )))
                        }
                      >
                        {faxTypeConfig.key === UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH && linkAndArchiveLoading
                          ? "Linking and Archiving..."
                          : faxTypeConfig.linkButtonLabel}
                      </Button>
                    </Col>
                    {faxTypeConfig.showDiscard && (
                      <Col md={6}>
                        {hasLinkedPages ? (
                          <CompleteLinkingButton
                            disallowEditing={isSubmitting || linkAndArchiveLoading}
                            completeLinking={completePageLinking}
                            faxId={faxInfoId}
                          />
                        ) : (
                          <DiscardFax
                            disallowEditing={isSubmitting}
                            discardFax={discardFax}
                            faxId={faxInfoId}
                          />
                        )}
                      </Col>
                    )}
                  </Row>
                  {faxTypeConfig.showMemberSwitchList && (
                    <MemberSwitchList
                      setSelectedSwitches={onChangeLinkSwitches}
                      values={values}
                      setValues={setValues}
                      resetTableSelection={resetSwitchSelection}
                      dataTestId="member-switch-list"
                    />
                  )}
                  {faxTypeConfig.showPrescriberList && (
                    <PrescriberListV2
                      setSelectedPrescribers={onChangeLinkPrescribers}
                      values={values}
                      setValues={setValues}
                      resetTableSelection={resetPrescriberSelection}
                      dataTestId="prescriber-list"
                    />
                  )}
                  {faxTypeConfig.showNotes && (
                    <>
                      <Form>
                        <Form.Group>
                          <Form.Label>Notes</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="notes"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                          />
                        </Form.Group>
                      </Form>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: "10px",
                        }}
                      >
                        {noteObject?.uflNoteUuid?.length > 0 ? (
                          <>
                            <Button
                              variant="primary"
                              onClick={handleSaveOrUpdateNote}
                              style={{ marginRight: "10px" }}
                              disabled={note.length === 0 || updateNoteLoading}
                            >
                              {updateNoteLoading ? "Updating..." : "Update Note"}
                            </Button>
                            <Button
                              variant="danger"
                              onClick={deleteNote}
                              disabled={deleteNoteLoading}
                            >
                              {deleteNoteLoading ? "Deleting..." : "Delete Note"}
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="primary"
                            onClick={handleSaveOrUpdateNote}
                            disabled={note.length === 0 || saveNoteLoading}
                          >
                            {saveNoteLoading ? "Saving..." : "Save Note"}
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </Form>
              )}
            </Formik>
              </>
            )}
          </Col>

          <Col md={7}>
          {!hasRoleIn([PERMISSIONS.PERM_PHI_ACCESS]) ? (
              <Container className="list-container" fluid>
                <Card variant="primary" className={"mb-3"}>
                  <Card.Title className="text-center"><h5>User does not have PHI access to view PDF</h5></Card.Title>
                  </Card>
              </Container>
            ) : (
              <ConfigurablePdfViewer
                height="85vh"
                width="100%"
                url={`/pdf/prescriber-inbound-fax/${faxInfoId}`}
                hiddenPages={allHiddenPages}
                showPageNumber={true}
              />
            )}
          </Col>
        </Row>
        <ConfirmationModal
          show={showConfirmModal}
          onHide={handleCancelLinkAndArchive}
          onConfirm={handleConfirmLinkAndArchive}
          title={faxTypeConfig.linkConfirmModal.title}
          body={faxTypeConfig.linkConfirmModal.body}
          confirmLabel="Yes"
          cancelLabel="No"
        />
      </Container>
    );
};

export default withRouter(UnprocessedFaxDetails);
